import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import ApplicationPage from "../components/ApplicationPage";
import LoadingAnimation from "../components/LoadingAnimation";
import { GET_SECTION_ANNOUNCEMENTS } from "../graphql/announcementsQueries";
import {
  AnnouncementType,
  PaginatedAnnouncements,
} from "../types/AnnoucementTypes";

const AnnouncementsPage = () => {
  const [page, setPage] = useState(1);
  const [announcements, setAnnouncements] = useState<AnnouncementType[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const { loading, error, fetchMore } = useQuery<
    PaginatedAnnouncements,
    { page: number; pageSize: number }
  >(GET_SECTION_ANNOUNCEMENTS, {
    variables: { page: 1, pageSize: 10 },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const { announcements, page, totalPages } = data.allAnnouncements;
      if (announcements && Array.isArray(announcements)) {
        setAnnouncements(announcements);
        setHasMore(page < totalPages);
      }
    },
  });

  const loadMoreAnnouncements = useCallback(() => {
    if (loading || !hasMore) return;

    const nextPage = page + 1;
    fetchMore({
      variables: { page: nextPage },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.allAnnouncements) {
          return prev;
        }

        const {
          announcements: newAnnouncements,
          page: newPage,
          totalPages,
        } = fetchMoreResult.allAnnouncements;

        setPage(newPage);
        setHasMore(newPage < totalPages);

        setAnnouncements((prevAnnouncements) => [
          ...prevAnnouncements,
          ...(newAnnouncements ?? []),
        ]);

        return prev;
      },
    });
  }, [fetchMore, hasMore, loading, page]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreAnnouncements();
        }
      },
      { rootMargin: "100px" },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [loadMoreAnnouncements, hasMore, loading]);

  if (error) return <div>Error loading announcements: {error.message}</div>;

  return (
    <ApplicationPage pageHeaderContent='Announcements'>
      {announcements.map((announcement, index) => (
        <div
          key={index}
          style={{
            padding: "10px",
            border: "1px solid #ccc",
            margin: "10px",
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: announcement.content }} />
        </div>
      ))}
      {loading && <LoadingAnimation text='Loading...' />}
      <div ref={loadMoreRef} />
    </ApplicationPage>
  );
};

export default AnnouncementsPage;
